/** Marks a conflict message built from raw constraint fields; clients key on it to replace them. */
export const RAW_CONFLICT_MESSAGE_PREFIX = "Conflict with existing resource";

export const structuredErrorResponseTypes = [
  "UniqueConstraintFailed",
  "InvalidData",
  "ConflictingEntity",
  "UnarchiveNameConflict",
] as const;
export type StructuredErrorResponseType = (typeof structuredErrorResponseTypes)[number];

export interface StructuredErrorResponse {
  type: StructuredErrorResponseType;
}

export interface UniqueConstraintError extends StructuredErrorResponse {
  type: "UniqueConstraintFailed";
  fields: string[];
}

export interface InvalidDataError extends StructuredErrorResponse {
  type: "InvalidData";
  fields: string[];
}

export interface ConflictingEntityError extends StructuredErrorResponse {
  type: "ConflictingEntity";
  entityIds: string[];
}

// Produced by the UnarchiveNameConflictError class in services/communities/communityTypes.ts.
export interface UnarchiveNameConflictBase extends StructuredErrorResponse {
  type: "UnarchiveNameConflict";
}

/** Renaming the location being unarchived resolves this one. */
export interface UnarchiveSelfNameConflictData extends UnarchiveNameConflictBase {
  conflictingName: string;
}

/** Locations restored by the cascade collide with active ones; only renaming those resolves it. */
export interface UnarchiveDescendantNameConflictData extends UnarchiveNameConflictBase {
  descendantConflictNames: string[];
}

export type UnarchiveNameConflictData = UnarchiveSelfNameConflictData | UnarchiveDescendantNameConflictData;

export type ApplicationError =
  | UniqueConstraintError
  | InvalidDataError
  | ConflictingEntityError
  | UnarchiveNameConflictData; // list all errors here

export interface DataErrorResponse {
  message: string;
  data: ApplicationError;
}

export function isUniqueConstraintError(error: ApplicationError | null | undefined): error is UniqueConstraintError {
  return error?.type === "UniqueConstraintFailed";
}

export function isInvalidDataError(error: ApplicationError | null | undefined): error is InvalidDataError {
  return error?.type === "InvalidData";
}

export function isConflictingEntityError(error: ApplicationError | null | undefined): error is ConflictingEntityError {
  return error?.type === "ConflictingEntity";
}

export function isUnarchiveNameConflictError(
  error: ApplicationError | null | undefined,
): error is UnarchiveNameConflictData {
  return error?.type === "UnarchiveNameConflict";
}

export function isUnarchiveSelfNameConflictError(
  error: ApplicationError | null | undefined,
): error is UnarchiveSelfNameConflictData {
  return isUnarchiveNameConflictError(error) && "conflictingName" in error;
}
